import React, { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { api } from "../../../services/api";
import { TextField, Box, Button, Alert, FormControl, Accordion, AccordionSummary, AccordionDetails, IconButton, Chip, } from '@mui/material';
import { ExpandMore, ModeEdit, Delete } from '@mui/icons-material';
import moment from "moment-timezone";
import BannerPreview from "../../../components/evento/BannerPreview";
import PublicacionPreview from "../../../components/evento/PublicacionPreview";

export const PublicarEvento = forwardRef(({ eventId, isPublic }, ref) => {
  const [formData, setFormData] = useState({
    eventoId: eventId,
    titulo: "",
    descripcion: "",
    desde: "",
    hasta: "",
  });
  const [eventoData, setEventoData] = useState(null);
  const [files, setFiles] = useState([]);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [mediaFormats, setMediaFormats] = useState([]);
  const [mediaId, setMediaId] = useState([]);
  const [fileNames, setFileNames] = useState([]);
  const [lengthDescrip, setLengthDescrip] = useState("");
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [pendingFaqs, setPendingFaqs] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));

    setLengthDescrip(value.length);
  };

  //carga de imagenes
  const uploadFiles = async (filesToUpload) => {
    const formData = new FormData();
    filesToUpload.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await api.media.upload(formData);
      if (response.status === "success") {
        const medias = response.data.media;
        setMediaUrls(prevUrls => [...prevUrls, ...medias.map(media => media.url)]);
        setMediaTypes(prevTypes => [...prevTypes, ...medias.map(media => media.type)]);
        setMediaFormats(prevFormats => [...prevFormats, ...medias.map(media => media.format)]);
        setMediaId(prevIds => [...prevIds, ...medias.map(media => media.public_id)]);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };

  const handleAddMediaFromPreview = async (files) => {
    setFiles((prevFiles) => {
      const combinedFiles = [...prevFiles, ...files];
      if (selectedFileIndex === null && files.length > 0) {
        setSelectedFileIndex(prevFiles.length);
      }
      return combinedFiles;
    });

    await uploadFiles(files);
  };

  //eliminacion de los attachs
  const deleteMedia = async (index) => {
    const publicId = mediaId[index];

    try {
      const response = await api.media.delete({ public_id: publicId });
      if (response.status === "success") {
        setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
        setFileNames(prevNames => prevNames.filter((_, i) => i !== index));
        setMediaUrls(prevUrls => prevUrls.filter((_, i) => i !== index));
        setMediaTypes(prevTypes => prevTypes.filter((_, i) => i !== index));
        setMediaFormats(prevFormats => prevFormats.filter((_, i) => i !== index));
        setMediaId(prevIds => prevIds.filter((_, i) => i !== index));

        if (selectedFileIndex === index) {
          setSelectedFileIndex(null);
        } else if (selectedFileIndex > index) {
          setSelectedFileIndex(prevIndex => prevIndex - 1);
        }
      }
    } catch (error) {
      console.error("Error deleting media:", error);
    }
  };

  //traemos el evento para rellenar los datos de la publi
  useEffect(() => {
    const loadEventData = async () => {
      try {
        const response = await api.eventos.get(eventId);
        if (response.status === "success") {
          setEventoData(response.data.evento);
        }
      } catch (error) {
        console.error("Error loading event data:", error);
      }
    };

    if (eventId) {
      loadEventData();
    }
  }, [eventId]);

  //actualizo el form si edito el preview
  const handlePreviewDataChange = (newFormData) => {
    setFormData(newFormData);
    setLengthDescrip(newFormData.descripcion?.length || 0);
  };

  //creamos la publicacion y las faqs todo cuando confirmas el evento
  const createPublicacionAndFaqs = async () => {
    try {
      // creamos la publicacion en caso de que se hayan rellenado datos
      if (formData.titulo || formData.descripcion || mediaUrls.length > 0) {
        if ((formData.titulo || formData.descripcion || mediaUrls.length > 0) && (!formData.desde || !formData.hasta)) {
          throw new Error("Las fechas de inicio y fin son obligatorias para la publicación.");
        }

        const desdeFormatted = moment.tz(formData.desde, "America/Buenos_Aires").format("YYYY-MM-DDT00:00:00-03:00");
        const hastaFormatted = moment.tz(formData.hasta, "America/Buenos_Aires").format("YYYY-MM-DDT00:00:00-03:00");

        //armamos los attachs
        const attachsData = Array.isArray(mediaUrls) && mediaUrls.length > 0
          ? mediaUrls.map((url, index) => ({
            url: url,
            type: Array.isArray(mediaTypes) ? mediaTypes[index] : 'image',
            format: Array.isArray(mediaFormats) ? mediaFormats[index] : 'jpg',
            public_id: Array.isArray(mediaId) ? mediaId[index] : null,
          }))
          : [];

        const publicacionData = {
          idevento: parseInt(eventId),
          titulo: formData.titulo || "",
          descripcion: formData.descripcion || "",
          desde: desdeFormatted,
          hasta: hastaFormatted,
          attachs: attachsData,
        };

        const publicacionResponse = await api.publicaciones.createPublicacion(publicacionData);

        if (publicacionResponse.status !== "success") {
          throw new Error("Error al crear la publicación");
        }
      }

      // creamos las faqs si es que hay
      let allFaqsToCreate = [...pendingFaqs];

      // si una faq esta editandose, la tomamos tambien
      if (showFAQSection && dataFAQS.pregunta?.trim() && dataFAQS.respuesta?.trim() && !editMode) {
        allFaqsToCreate.push({
          id: Date.now(),
          pregunta: dataFAQS.pregunta,
          respuesta: dataFAQS.respuesta,
          isPending: true
        });
      }

      if (allFaqsToCreate.length > 0) {
        //mando las faqs      
        for (const faq of allFaqsToCreate) {
          if (faq?.pregunta?.trim() && faq?.respuesta?.trim()) {
            const eventoIdNum = parseInt(eventId);
            if (!eventoIdNum || eventoIdNum === 0) {
              throw new Error(`ID de evento inválido: ${eventId}`);
            }

            await api.faqs.createFaqs(eventoIdNum, {
              pregunta: faq.pregunta,
              respuesta: faq.respuesta
            });
          }
        }

        // limpio todo despues de crear
        setPendingFaqs([]);
        setDataFAQS(dataFAQSInit);
        setShowFAQSection(false);
      }

      return { success: true };
    } catch (error) {
      console.error("Error creating publication and FAQs:", error);
      return { success: false, error: error.message };
    }
  };

  //exponemos la fx para que el stepper la use
  useImperativeHandle(ref, () => ({
    createPublicacionAndFaqs
  }));

  //FAQS
  const [dataFAQSInit, setDataFAQSInit] = useState({
    idevento: eventId,
    pregunta: "",
    respuesta: "",
  });
  const [dataFAQS, setDataFAQS] = useState(dataFAQSInit);
  const [errorFAQS, setErrorFAQS] = useState({})
  const [showFAQSection, setShowFAQSection] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editFAQId, setEditFAQId] = useState(null);

  const handleChangeFAQS = (e) => {
    const { name, value } = e.target;
    setDataFAQS({
      ...dataFAQS,
      [name]: value,
    });
    setErrorFAQS((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleSubmitFAQS = async (e) => {
    e.preventDefault();
    let validationErrors = {};

    if (!dataFAQS.pregunta.trim()) {
      validationErrors.pregunta = "La pregunta es obligatoria";
    }

    if (!dataFAQS.respuesta.trim()) {
      validationErrors.respuesta = "La respuesta es obligatoria";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrorFAQS(validationErrors);
      return;
    }

    if (editMode) {
      try {
        const response = await api.faqs.editFaqs(eventId, editFAQId, dataFAQS);
        if (response.status === 'success') {
          setEditMode(false);
          setEditFAQId(null);
        }
      } catch (error) {
        console.error("Error al editar la FAQ:", error);
      }
    } else {
      const newFaq = {
        id: Date.now(),
        pregunta: dataFAQS.pregunta,
        respuesta: dataFAQS.respuesta,
        isPending: true
      };

      setPendingFaqs(prev => [...prev, newFaq]);
    }

    setDataFAQS(dataFAQSInit);
    setShowFAQSection(false);
  };

  const deletePendingFAQ = (id) => {
    setPendingFaqs(prev => prev.filter(faq => faq.id !== id));
  };

  const editPendingFAQ = (faq) => {
    setDataFAQS({
      idevento: eventId,
      pregunta: faq.pregunta,
      respuesta: faq.respuesta,
    });
    deletePendingFAQ(faq.id);
    setShowFAQSection(true);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "30px", padding: "0 10px" }}>
        <div style={{ maxWidth: "700px", width: "100%" }}>

          {/* CARD EDITABLE */}
          <div style={{ marginBottom: "30px" }}>
            <h3>Vista previa de tu publicación</h3>
            <p> Así se verá tu evento en nuestro inicio.</p>
            <PublicacionPreview
              evento={eventoData}
              formData={formData}
              onFormDataChange={handlePreviewDataChange}
              mediaUrls={mediaUrls}
              onAddMedia={handleAddMediaFromPreview}
              onDeleteMedia={deleteMedia}
            />
          </div>

          <div>
            {/* si hay info en la publicacion, mostrar inputs de fecha de inicio y fin de la publi */}
            {(formData.titulo || formData.descripcion || mediaUrls.length > 0) && (
              <div className="mb-3">
                <p className="subtitulo-seccion-evento-expanded">
                  Periodo de la publicación <span className="text-danger">*</span>
                </p>
                <p className="descrip-seccion-evento-expanded mb-0 text-justify">
                  Selecciona las fechas en las que deseas que tu publicación sea visible.
                  La publicación comenzará a mostrarse en la fecha de inicio y dejará de estar disponible después de la fecha de finalización.
                </p>
                <div className="d-flex flex-row gap-4 flex-wrap mt-1">
                  <FormControl sx={{ width: "35%" }}>
                    <TextField
                      sx={{
                        marginTop: "5px",
                        marginBottom: "15px",
                        input: { padding: "10px 15px" }
                      }}
                      id="desde"
                      name="desde"
                      type="date"
                      value={formData.desde}
                      onChange={handleInputChange}
                      required
                    />
                    <small style={{ position: "relative", top: "-10px" }}>Fecha de inicio</small>
                  </FormControl>
                  <FormControl sx={{ width: "35%" }}>
                    <TextField
                      sx={{
                        marginTop: "5px",
                        marginBottom: "15px",
                        input: { padding: "10px 15px" }
                      }}
                      id="hasta"
                      name="hasta"
                      type="date"
                      value={formData.hasta}
                      onChange={handleInputChange}
                      required
                    />
                    <small style={{ position: "relative", top: "-10px" }}>Fecha de finalización</small>
                  </FormControl>
                  {/* si hay contenido y no fechas, error */}
                  {(formData.titulo || formData.descripcion || mediaUrls.length > 0) &&
                    (!formData.desde || !formData.hasta) && (
                      <Alert severity="warning" sx={{ width: "100%" }}>
                        Las fechas son obligatorias si deseas crear una publicación.
                      </Alert>
                    )}
                </div>
              </div>
            )}

            {/* FAQS */}
            <Box component="form" id="formFAQS" onSubmit={handleSubmitFAQS} sx={{ width: "100%", marginTop: "30px" }}>
              <Box sx={{ marginBottom: "7px", display: "flex", alignItems: "center", gap: "5px" }}>
                <p className="subtitulo-seccion-evento-expanded m-0">Preguntas frecuentes</p>
                <Chip label="Opcional" variant="outlined" size="small" />
              </Box>

              <p className="descrip-seccion-evento-expanded mb-0">
                Agrega preguntas frecuentes personalizadas para ayudar a las personas interesadas en tu evento a resolver dudas comunes.
              </p>

              {!showFAQSection ? (
                <Box sx={{ display: "flex", justifyContent: "end" }}>
                  <Button
                    variant="text"
                    onClick={() => setShowFAQSection(true)}
                    sx={{ marginBottom: "20px", fontWeight: 500 }}
                  >
                    + Agregar preguntas
                  </Button>
                </Box>
              ) : (
                <>
                  <FormControl sx={{ width: "100%", marginTop: "7px" }}>
                    <TextField
                      sx={{
                        marginTop: "5px",
                        input: {
                          padding: "10px 15px",
                        },
                      }}
                      type="text"
                      placeholder="Pregunta"
                      name="pregunta"
                      value={dataFAQS.pregunta}
                      onChange={handleChangeFAQS}
                      error={Boolean(errorFAQS.pregunta)}
                    />
                    <TextField
                      sx={{
                        marginTop: "5px",
                        input: {
                          padding: "10px 15px",
                        },
                      }}
                      multiline
                      rows={2}
                      placeholder="Respuesta"
                      name="respuesta"
                      value={dataFAQS.respuesta}
                      onChange={handleChangeFAQS}
                      error={Boolean(errorFAQS.respuesta)}
                    />
                  </FormControl>
                  {errorFAQS.pregunta && (
                    <Alert severity="error" sx={{ marginTop: "5px" }}>
                      {errorFAQS.pregunta}
                    </Alert>
                  )}
                  {errorFAQS.respuesta && (
                    <Alert severity="error" sx={{ marginTop: "5px" }}>
                      {errorFAQS.respuesta}
                    </Alert>
                  )}
                  <div className="d-flex justify-content-end mb-2">
                    <Button variant="text" type="submit" className="fw-medium">
                      Crear pregunta
                    </Button>
                  </div>
                </>
              )}

              {pendingFaqs.length > 0 && pendingFaqs.map((faq) => (
                <Accordion key={faq.id} style={{ backgroundColor: faq.isPending ? "#fff3cd" : "#f9f9f9", marginBottom: "7px" }}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="panel1-content"
                    id={`panel1-header-${faq.id}`}
                    sx={{ width: "100%", fontSize: "14px", fontWeight: "500" }}
                  >
                    <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                      <span>{faq.pregunta}</span>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "7px", marginLeft: "7px" }}>
                        <IconButton
                          sx={{ padding: "0px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            editPendingFAQ(faq);
                          }}
                        >
                          <ModeEdit sx={{ fontSize: "19px" }} />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePendingFAQ(faq.id);
                          }}
                          sx={{ padding: "0px" }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>

                  </AccordionSummary>
                  <AccordionDetails sx={{ fontSize: "14px" }}>
                    {faq.respuesta}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </div>
        </div >
      </div >
      {isPublic && eventId && <BannerPreview eventId={eventId} />}
    </div>
  )
});